import moment from "moment";

/*
 * Helpers para mostrar el detalle (renglones) de una nota de entrega en forma
 * de "ticket": una linea por producto con su codigo, nombre, cantidad, precio
 * unitario, IVA e importe, mas los totales.
 *
 * Se reutiliza tanto en el modal de auditoria (TablaDetalleNota) como en el
 * reporte PDF (reporteNotaEntregaRespaldoPDF), por eso vive en su propio
 * modulo y no contiene JSX.
 */

/* Etiquetas amigables de los campos de la cabecera de la nota */
const ETIQUETAS_NOTA = {
  numnotaentrega: "N° Nota",
  fecha: "Fecha",
  hora: "Hora",
  cedularif: "Cédula / RIF",
  idcliente: "Cliente",
  idvendedor: "Vendedor",
  idalmacen: "Almacén",
  idmoneda: "Moneda",
  idpedido: "Pedido",
  idcaja: "Caja",
  idoperacioninventario: "Operación inventario",
  totalmonto: "Total",
  totaliva: "IVA",
  totaldescuento: "Descuento",
  status: "Status",
};

/* Orden en que se muestran los campos de la cabecera (solo los que existan) */
const ORDEN_NOTA = [
  "numnotaentrega",
  "fecha",
  "hora",
  "cedularif",
  "idcliente",
  "idvendedor",
  "idalmacen",
  "idmoneda",
  "idpedido",
  "idcaja",
  "idoperacioninventario",
  "totalmonto",
  "totaliva",
  "totaldescuento",
  "status",
];

/* Campos de cabecera que se formatean como monto */
const CAMPOS_MONTO = ["totalmonto", "totaliva", "totaldescuento"];

/* Columnas del ticket (una linea por producto) */
export const COLUMNAS_DETALLE = [
  { key: "codigo", label: "Código", align: "left", flex: 1.3 },
  { key: "producto", label: "Producto", align: "left", flex: 3.4 },
  { key: "cantidad", label: "Cant.", align: "right", flex: 0.9, tipo: "cantidad" },
  { key: "punitario", label: "P. Unit.", align: "right", flex: 1.3, tipo: "monto" },
  { key: "iva", label: "IVA %", align: "right", flex: 0.8, tipo: "cantidad" },
  { key: "importe", label: "Importe", align: "right", flex: 1.4, tipo: "monto" },
];

const numero = (valor) => {
  if (valor === null || valor === undefined || valor === "") {
    return null;
  }
  const n =
    typeof valor === "number" ? valor : parseFloat(String(valor).replace(",", "."));
  return isNaN(n) ? null : n;
};

/* Da formato de monto (2 decimales) a un valor numerico */
export const formatearMonto = (valor) => {
  const n = numero(valor);
  if (n === null) {
    return valor === null || valor === undefined || valor === "" ? "-" : String(valor);
  }
  return n.toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/* Da formato de cantidad (entera sin decimales, con decimales si aplica) */
export const formatearCantidad = (valor) => {
  const n = numero(valor);
  if (n === null) {
    return valor === null || valor === undefined || valor === "" ? "-" : String(valor);
  }
  if (Number.isInteger(n)) {
    return String(n);
  }
  return n.toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatearFecha = (valor) => {
  if (!valor) {
    return valor;
  }
  const m = moment(valor);
  return m.isValid() ? m.format("DD-MM-YYYY") : String(valor);
};

/* Convierte el detalle (arreglo o texto JSON) en un arreglo de objetos */
export const parsearDetalle = (valor) => {
  if (Array.isArray(valor)) {
    return valor;
  }
  if (typeof valor === "string" && valor.trim() !== "") {
    try {
      const parseado = JSON.parse(valor);
      return Array.isArray(parseado) ? parseado : [];
    } catch (error) {
      return [];
    }
  }
  return [];
};

/* Toma el primer valor no vacio de la lista de claves */
const tomar = (objeto, claves) => {
  for (let i = 0; i < claves.length; i++) {
    const valor = objeto[claves[i]];
    if (valor !== undefined && valor !== null && valor !== "") {
      return valor;
    }
  }
  return "";
};

/* Campos de cabecera de la nota listos para mostrar [{ label, valor }] */
export const camposCabeceraNota = (nota) => {
  const objeto = nota && typeof nota === "object" ? nota : {};
  const usadas = {};
  const campos = [];

  ORDEN_NOTA.forEach((clave) => {
    if (objeto[clave] === undefined || objeto[clave] === null) {
      return;
    }
    usadas[clave] = true;
    let valor = objeto[clave];
    if (clave === "fecha") {
      valor = formatearFecha(valor);
    } else if (CAMPOS_MONTO.indexOf(clave) >= 0) {
      valor = formatearMonto(valor);
    }
    campos.push({ label: ETIQUETAS_NOTA[clave], valor });
  });

  /* Campos que no estén en la lista se muestran al final (nombre tecnico) */
  Object.keys(objeto).forEach((clave) => {
    if (usadas[clave]) {
      return;
    }
    const valor = objeto[clave];
    if (valor === null || valor === undefined) {
      return;
    }
    campos.push({ label: clave, valor });
  });

  return campos;
};

/* Convierte un renglon del detalle a la fila del ticket */
export const filaTicket = (item) => {
  const row = item && typeof item === "object" ? item : {};
  const cantidad = numero(row.cantidad) || 0;
  const punitario = numero(row.punitario) || 0;

  return {
    idproducto: row.idproducto,
    codigo: tomar(row, ["codigo", "codigoproducto", "codigoprod"]),
    producto:
      tomar(row, ["producto", "nombre", "nombrecorto", "descripcion"]) ||
      "Producto " + (row.idproducto !== undefined ? row.idproducto : ""),
    unidad: tomar(row, ["unidad", "descripcionunidad", "descripcionuni"]),
    cantidad: cantidad,
    punitario: punitario,
    iva: tomar(row, ["iva"]),
    importe: cantidad * punitario,
  };
};

/*
 * Arma las filas y los totales del ticket a partir del detalle (arreglo o
 * texto JSON). Devuelve { filas, totales }.
 */
export const detalleTicket = (valor) => {
  const filas = parsearDetalle(valor).map(filaTicket);

  const totales = filas.reduce(
    (acc, fila) => {
      acc.cantidad += fila.cantidad;
      acc.importe += fila.importe;
      return acc;
    },
    { cantidad: 0, importe: 0 }
  );

  return { filas, totales };
};

/* Texto de una celda segun el tipo de columna */
export const celdaTexto = (columna, fila) => {
  const valor = fila[columna.key];
  if (columna.tipo === "monto") {
    return formatearMonto(valor);
  }
  if (columna.tipo === "cantidad") {
    return formatearCantidad(valor);
  }
  return valor === null || valor === undefined || valor === "" ? "-" : String(valor);
};



// WEBPACK FOOTER //
// ./src/components/configuracionGeneral/detalleNotaEntrega.js